import { useCallback, useEffect, useRef, useState } from 'react';
import { useCouponsStatistics } from '../../../hooks/useStatistics';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import {
  GERENCIALES_REPORT_OPTIONS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_MESSAGES,
  GERENCIALES_DEBT_STATUS_REPORT_TYPE,
} from '../constants/gerencialesConstants';

/**
 * Obtiene el rango del mes actual en formato YYYY-MM-DD.
 * @returns {{dateStart: string, dateEnd: string}} Rango del mes actual.
 */
const getMonthDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const toDateString = (dateValue) => dateValue.toISOString().split('T')[0];

  return {
    dateStart: toDateString(start),
    dateEnd: toDateString(end),
  };
};

/**
 * Hook para obtener y mantener estadisticas gerenciales de contratos.
 * @returns {Object} Estado y acciones para la vista Gerenciales.
 */
export const useGerencialesStatistics = () => {
  const { mutateAsync: fetchStatistics } = useCouponsStatistics();
  const { data: paymentMethods = [], isLoading: paymentMethodsLoading } = usePaymentMethods();
  const [reportType, setReportType] = useState(GERENCIALES_REPORT_TYPE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [header, setHeader] = useState(null);
  const [filters, setFilters] = useState(() => getMonthDateRange());
  const [debtStatusFilters, setDebtStatusFilters] = useState({
    possessor: 'all',
    orderBy: 'importe_mora_desc',
    minDebtAmount: '',
    maxDebtAmount: '',
    paymentMethod: '',
    contractNumber: '',
  });
  const [debtStatusPagination, setDebtStatusPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    total: 0,
    pages: 0,
  });
  const loadStatisticsRef = useRef(null);
  const lastReportTypeRef = useRef(null);

  const requiresDateRange = GERENCIALES_DATE_RANGE_REPORTS.includes(reportType);

  /**
   * Maneja el cambio de filtros de fecha.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del input.
   * @returns {void} Actualiza el filtro.
   */
  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Solicita al backend las estadisticas gerenciales.
   * @returns {Promise<void>} Promesa de carga.
   */
  const loadStatistics = useCallback(async (overrides = {}) => {
    const pageOverride = overrides.page ?? debtStatusPagination.page;
    const rowsPerPageOverride = overrides.rowsPerPage ?? debtStatusPagination.rowsPerPage;

    setLoading(true);
    setError(null);

    try {
      if (requiresDateRange) {
        if (!filters.dateStart || !filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.DATES_REQUIRED);
          setResults(null);
          setHeader(null);
          return;
        }
        if (filters.dateStart > filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.INVALID_RANGE);
          setResults(null);
          setHeader(null);
          return;
        }
      }

      const payload = { reportType };
      if (requiresDateRange) {
        payload.dateStart = filters.dateStart;
        payload.dateEnd = filters.dateEnd;
      }
      if (reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE) {
        payload.isPossessor = debtStatusFilters.possessor;
        payload.orderBy = debtStatusFilters.orderBy;
        if (debtStatusFilters.minDebtAmount !== '') {
          payload.minDebtAmount = Number(debtStatusFilters.minDebtAmount);
        }
        if (debtStatusFilters.maxDebtAmount !== '') {
          payload.maxDebtAmount = Number(debtStatusFilters.maxDebtAmount);
        }
        if (debtStatusFilters.paymentMethod) {
          payload.paymentMethod = debtStatusFilters.paymentMethod;
        }
        if (debtStatusFilters.contractNumber) {
          payload.contractNumber = debtStatusFilters.contractNumber.trim();
        }
        payload.page = pageOverride;
        payload.perPage = rowsPerPageOverride;
      }

      const response = await fetchStatistics(payload);
      setResults(response?.results || null);
      setHeader(response?.header || null);
      if (reportType === GERENCIALES_DEBT_STATUS_REPORT_TYPE) {
        const pagination = response?.results?.pagination || {};
        setDebtStatusPagination((prev) => ({
          ...prev,
          total: pagination.total ?? response?.header?.total_contracts ?? 0,
          pages: pagination.pages ?? response?.header?.pages ?? 0,
          page: pagination.page ?? pageOverride,
          rowsPerPage: pagination.per_page ?? rowsPerPageOverride,
        }));
      }
    } catch (fetchError) {
      console.error('Error fetching managerial statistics:', fetchError);
      setError('Error al obtener las estadisticas.');
      setResults(null);
      setHeader(null);
    } finally {
      setLoading(false);
    }
  }, [
    fetchStatistics,
    reportType,
    requiresDateRange,
    filters.dateStart,
    filters.dateEnd,
    debtStatusFilters.possessor,
    debtStatusFilters.orderBy,
    debtStatusFilters.minDebtAmount,
    debtStatusFilters.maxDebtAmount,
    debtStatusFilters.paymentMethod,
    debtStatusFilters.contractNumber,
    debtStatusPagination.page,
    debtStatusPagination.rowsPerPage,
  ]);

  useEffect(() => {
    loadStatisticsRef.current = loadStatistics;
  }, [loadStatistics]);

  useEffect(() => {
    if (lastReportTypeRef.current === reportType) return;
    lastReportTypeRef.current = reportType;
    if (loadStatisticsRef.current) {
      loadStatisticsRef.current();
    }
  }, [reportType]);

  /**
   * Maneja el cambio de tipo de reporte.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza el tipo de reporte.
   */
  const handleReportTypeChange = (event) => {
    const { value } = event.target;
    const isValid = GERENCIALES_REPORT_OPTIONS.some((option) => option.value === value);
    if (!isValid) return;
    setReportType(value);
    setResults(null);
    setHeader(null);
    if (GERENCIALES_DATE_RANGE_REPORTS.includes(value)) {
      setFilters(getMonthDateRange());
    } else {
      setFilters({ dateStart: '', dateEnd: '' });
    }
    if (value === GERENCIALES_DEBT_STATUS_REPORT_TYPE) {
      setDebtStatusFilters({
        possessor: 'all',
        orderBy: 'importe_mora_desc',
        minDebtAmount: '',
        maxDebtAmount: '',
        paymentMethod: '',
        contractNumber: '',
      });
      setDebtStatusPagination({
        page: 1,
        rowsPerPage: 10,
        total: 0,
        pages: 0,
      });
    }
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de filtros para el reporte de mora.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza filtros de mora.
   */
  const handleDebtStatusFilterChange = (event) => {
    const { name, value } = event.target;
    setDebtStatusFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setDebtStatusPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de página del reporte de mora.
   * @param {import('react').ChangeEvent<unknown>} _event - Evento de paginación.
   * @param {number} value - Página seleccionada.
   * @returns {void} Actualiza la página.
   */
  const handleDebtStatusPageChange = (_event, value) => {
    setDebtStatusPagination((prev) => ({
      ...prev,
      page: value,
    }));
    loadStatistics({ page: value });
  };

  /**
   * Maneja el cambio de cantidad de filas del reporte de mora.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza la cantidad de filas.
   */
  const handleDebtStatusRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setDebtStatusPagination((prev) => ({
        ...prev,
        page: 1,
        rowsPerPage: value,
      }));
      loadStatistics({ page: 1, rowsPerPage: value });
    }
  };

  /**
   * Obtiene data completa para exportar el reporte de mora.
   * @returns {Promise<Array<Object>>} Filas completas del reporte.
   */
  const fetchDebtStatusExport = async () => {
    const total = debtStatusPagination.total || 0;
    const perPage = total > 0 ? total : 1000;

    const payload = {
      reportType: GERENCIALES_DEBT_STATUS_REPORT_TYPE,
      isPossessor: debtStatusFilters.possessor,
      orderBy: debtStatusFilters.orderBy,
      ...(debtStatusFilters.minDebtAmount !== ''
        ? { minDebtAmount: Number(debtStatusFilters.minDebtAmount) }
        : {}),
      ...(debtStatusFilters.maxDebtAmount !== ''
        ? { maxDebtAmount: Number(debtStatusFilters.maxDebtAmount) }
        : {}),
      ...(debtStatusFilters.paymentMethod
        ? { paymentMethod: debtStatusFilters.paymentMethod }
        : {}),
      ...(debtStatusFilters.contractNumber
        ? { contractNumber: debtStatusFilters.contractNumber.trim() }
        : {}),
      page: 1,
      perPage,
    };

    const response = await fetchStatistics(payload);
    return response?.results?.table || [];
  };

  return {
    reportType,
    loading,
    error,
    results,
    header,
    filters,
    debtStatusFilters,
    debtStatusPagination,
    paymentMethods,
    paymentMethodsLoading,
    reload: loadStatistics,
    handleReportTypeChange,
    handleDateChange,
    handleDebtStatusFilterChange,
    handleDebtStatusPageChange,
    handleDebtStatusRowsPerPageChange,
    fetchDebtStatusExport,
  };
};
